import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { pricePrintLine, printVehicleKey } from '@wrapforce/shared';
import type { PrintPricingInput } from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { useLocaleFormatters } from '@/lib/format';
import type { DraftLine } from '../NewQuotePage';
import { usePrintDesigns } from '@/features/print-wraps/api';
import { useWrapPricingProfile, useVehicleCatalogTrimDetail, useWorkspaceVehicleById } from './pricingApi';
import { useBrands, useInventory } from '@/features/inventory/api';
import {
  VehiclePicker, vehicleSelectionFromTrimDetail, vehicleSelectionFromWorkspaceVehicleItem,
  type CustomerVehicleHint, type VehicleSelection,
} from './VehiclePicker';
import { PricingBreakdownSidebar } from './PricingBreakdownSidebar';
import { AddInventoryItemDialog } from './AddInventoryItemDialog';

// Same removal-minute assumptions the wrap engine uses (VehicleAwareWrapLineBuilder.tsx) -
// print lines reuse the wrap profile version and its removal model, per the spec.
const REMOVAL_MINUTES_BY_TIER = { light: 150, moderate: 360, heavy_baked: 720 };

export const PRINT_COVERAGE_LABEL_KEYS: Record<'full' | 'three_quarter' | 'half' | 'spot', string> = {
  full: 'pricing.print.coverageLevel.full',
  three_quarter: 'pricing.print.coverageLevel.three_quarter',
  half: 'pricing.print.coverageLevel.half',
  spot: 'pricing.print.coverageLevel.spot',
};

export interface PrintLineOutput {
  serviceKind: 'print';
  estimatedMinutes: number;
  lineTotalCents: number;
  breakdown: Record<string, unknown>;
  profileVersionId: string;
  vehicleLabel: string;
  vehicleCatalogTrimId: string | null;
  workspaceVehicleId: string | null;
  materialInventoryItemId: string | null;
  materialQty: number | null;
  materialUnit: 'sqft' | null;
  reservedMaterialCostCents: number | null;
  designId: string | null;
  designName: string | null;
  coverageLevel: 'full' | 'three_quarter' | 'half' | 'spot';
  spotFtX10: number;
  spotMinutes: number;
  laminateInventoryItemId: string | null;
  printCostCentsPerSqft: number;
  designMode: 'flat' | 'hourly' | 'waived';
  designFlatCents: number;
  designMinutes: number;
  revisionRounds: number;
  removalTier: 'none' | 'light' | 'moderate' | 'heavy_baked';
  fixedAddOnCents: number;
}

export interface PrintDraftState {
  vehicle: VehicleSelection | null;
  designId: string | null;
  coverageLevel: 'full' | 'three_quarter' | 'half' | 'spot';
  spotSqft: string;
  spotMinutes: string;
  printMediaItemId: string;
  laminateItemId: string;
  printCostPerSqft: string;
  designMode: 'flat' | 'hourly' | 'waived';
  designFlatCents: string;
  designMinutes: string;
  revisionRounds: string;
  removalTier: 'none' | 'light' | 'moderate' | 'heavy_baked';
  fixedAddOnCents: string;
}

export function PrintLineBuilder({
  workspaceId, onAddLine, initialState, onStateChange, customerVehicleHint = null, customerId = null, editingLine = null, onCancelEdit,
}: {
  workspaceId: string | null;
  onAddLine: (line: PrintLineOutput) => void;
  initialState?: Record<string, unknown>;
  onStateChange?: (state: Record<string, unknown>) => void;
  customerVehicleHint?: CustomerVehicleHint | null;
  customerId?: string | null;
  editingLine?: DraftLine | null;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const navigate = useNavigate();
  // Print lines are priced off the wrap profile version (rates, waste, markup)
  // and the wrap engine's vehicle base ft/minutes/complexity - see PRINT_VIEWS
  // spec's pricing/print.ts.
  const { data: profile } = useWrapPricingProfile(workspaceId);

  const editingCoverage = editingLine?.coverage?.serviceKind === 'print' ? editingLine.coverage : null;
  const restore = (editingLine ? undefined : initialState) as Partial<PrintDraftState> | undefined;

  const [vehicle, setVehicle] = useState<VehicleSelection | null>(restore?.vehicle ?? null);
  const [designId, setDesignId] = useState<string | null>(editingCoverage?.designId ?? restore?.designId ?? null);
  const [coverageLevel, setCoverageLevel] = useState<'full' | 'three_quarter' | 'half' | 'spot'>(editingCoverage?.coverageLevel ?? restore?.coverageLevel ?? 'full');
  const [spotSqft, setSpotSqft] = useState<string>(editingCoverage ? String((editingCoverage.spotFtX10 ?? 0) / 10) : restore?.spotSqft ?? '');
  const [spotMinutes, setSpotMinutes] = useState<string>(editingCoverage ? String(editingCoverage.spotMinutes ?? 0) : restore?.spotMinutes ?? '');
  const [printMediaItemId, setPrintMediaItemId] = useState<string>(editingLine?.materialInventoryItemId ?? restore?.printMediaItemId ?? '');
  const [laminateItemId, setLaminateItemId] = useState<string>(editingCoverage?.laminateInventoryItemId ?? restore?.laminateItemId ?? '');
  const [printCostPerSqft, setPrintCostPerSqft] = useState<string>(editingCoverage ? String((editingCoverage.printCostCentsPerSqft ?? 0) / 100) : restore?.printCostPerSqft ?? '0');
  const [designMode, setDesignMode] = useState<'flat' | 'hourly' | 'waived'>(editingCoverage?.designMode ?? restore?.designMode ?? 'waived');
  const [designFlatCents, setDesignFlatCents] = useState<string>(editingCoverage ? String((editingCoverage.designFlatCents ?? 0) / 100) : restore?.designFlatCents ?? '0');
  const [designMinutes, setDesignMinutes] = useState<string>(editingCoverage ? String(editingCoverage.designMinutes ?? 0) : restore?.designMinutes ?? '0');
  const [revisionRounds, setRevisionRounds] = useState<string>(
    editingCoverage ? String(editingCoverage.revisionRounds ?? 0) : restore?.revisionRounds ?? (profile ? String(profile.resolved.revisionRoundsIncluded) : '2'),
  );
  const [removalTier, setRemovalTier] = useState<'none' | 'light' | 'moderate' | 'heavy_baked'>(editingCoverage?.removalTier ?? restore?.removalTier ?? 'none');
  const [fixedAddOnCents, setFixedAddOnCents] = useState<string>(editingCoverage ? String((editingCoverage.fixedAddOnCents ?? 0) / 100) : restore?.fixedAddOnCents ?? '0');
  const [addInventoryOpen, setAddInventoryOpen] = useState<'media' | 'laminate' | null>(null);

  // Same rationale as the other vehicle-aware builders: a saved line only
  // carries the vehicle's ids, so refetch its full VehicleSelection once.
  const { data: trimDetail } = useVehicleCatalogTrimDetail(workspaceId, editingLine?.vehicleCatalogTrimId ?? undefined);
  const { data: workspaceVehicleDetail } = useWorkspaceVehicleById(workspaceId, editingLine?.workspaceVehicleId ?? undefined);
  const resolvingEditVehicle = !!(editingLine?.vehicleCatalogTrimId || editingLine?.workspaceVehicleId) && !vehicle;

  useEffect(() => {
    if (vehicle) return;
    const resolved = trimDetail
      ? vehicleSelectionFromTrimDetail(trimDetail)
      : workspaceVehicleDetail
        ? vehicleSelectionFromWorkspaceVehicleItem(workspaceVehicleDetail)
        : null;
    if (resolved) setVehicle(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDetail, workspaceVehicleDetail]);

  // Printed-wrap designs are keyed by the customer's actual year/make/model
  // (see print-wraps feature), not the size-class trim VehiclePicker resolves -
  // customerVehicleHint (step 2's vehicle) is the source of truth for that key.
  const vehicleKey = customerVehicleHint ? printVehicleKey(customerVehicleHint.year, customerVehicleHint.make, customerVehicleHint.model) : null;
  const { data: designsData } = usePrintDesigns(workspaceId, vehicleKey ? { vehicleKey } : undefined);
  const designs = designsData?.items ?? [];
  const selectedDesign = designs.find((d) => d.id === designId) ?? null;

  useEffect(() => {
    if (!selectedDesign) return;
    setCoverageLevel(selectedDesign.coverageLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDesign?.id]);

  const { data: inventoryData } = useInventory(workspaceId, { category: 'print' });
  const printMediaItems = inventoryData?.items ?? [];
  const { data: brandsData } = useBrands(workspaceId);
  const brandNameById = new Map((brandsData?.items ?? []).map((b): [string, string] => [b.id, b.name]));
  const itemLabel = (id: string) => {
    const item = printMediaItems.find((i) => i.id === id);
    if (!item) return '';
    const brand = (item.brandId && brandNameById.get(item.brandId)) || '';
    return brand ? `${brand} - ${item.name}` : item.name;
  };

  useEffect(() => {
    if (!inventoryData) return;
    if (printMediaItemId && printMediaItems.some((i) => i.id === printMediaItemId)) return;
    setPrintMediaItemId(printMediaItems[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const printMediaItem = printMediaItems.find((i) => i.id === printMediaItemId) ?? null;
  const laminateItem = printMediaItems.find((i) => i.id === laminateItemId) ?? null;

  const input: PrintPricingInput | null = useMemo(() => {
    if (!vehicle || !profile) return null;
    return {
      laborRateCentsPerHour: profile.resolved.laborRateCentsPerHour,
      designRateCentsPerHour: profile.resolved.designRateCentsPerHour,
      wasteBp: profile.resolved.wasteBp,
      markupBp: profile.resolved.markupBp,
      vehicleBaseFtX10: vehicle.baseFtX10,
      vehicleBaseMinutes: vehicle.baseMinutes,
      complexityBp: vehicle.complexityBp,
      coverageLevel,
      spotFtX10: coverageLevel === 'spot' ? Math.round((Number(spotSqft) || 0) * 10) : 0,
      spotMinutes: coverageLevel === 'spot' ? Math.round(Number(spotMinutes) || 0) : 0,
      printMediaCentsPerSqft: printMediaItem?.priceCentsPerUnit ?? 0,
      laminateCentsPerSqft: laminateItem?.priceCentsPerUnit ?? 0,
      printCostCentsPerSqft: Math.round((Number(printCostPerSqft) || 0) * 100),
      designMode,
      designFlatCents: Math.round((Number(designFlatCents) || 0) * 100),
      designMinutes: Math.round(Number(designMinutes) || 0),
      removalTier,
      removalMinutesByTier: REMOVAL_MINUTES_BY_TIER,
      fixedAddOnCents: Math.round((Number(fixedAddOnCents) || 0) * 100),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicle, profile, coverageLevel, spotSqft, spotMinutes, printMediaItem, laminateItem,
    printCostPerSqft, designMode, designFlatCents, designMinutes, removalTier, fixedAddOnCents,
  ]);

  const result = input ? pricePrintLine(input) : null;

  const spotValid = coverageLevel !== 'spot' || (Number(spotSqft) > 0);
  const canAdd = !!vehicle && !!profile && !!result && !!printMediaItemId && spotValid;

  const liveOutput: PrintLineOutput | null = canAdd && vehicle && profile && result && input
    ? {
        serviceKind: 'print',
        estimatedMinutes: result.estimatedMinutes,
        lineTotalCents: result.lineSubtotalCents,
        breakdown: result.breakdown as unknown as Record<string, unknown>,
        profileVersionId: profile.versionId,
        vehicleLabel: vehicle.label,
        vehicleCatalogTrimId: vehicle.catalogTrimId,
        workspaceVehicleId: vehicle.workspaceVehicleId,
        materialInventoryItemId: printMediaItemId || null,
        materialQty: result.filmFtX10 / 10,
        materialUnit: 'sqft',
        reservedMaterialCostCents: result.materialCostCents > 0 ? result.materialCostCents : null,
        designId,
        designName: selectedDesign?.name ?? null,
        coverageLevel,
        spotFtX10: input.spotFtX10,
        spotMinutes: input.spotMinutes,
        laminateInventoryItemId: laminateItemId || null,
        printCostCentsPerSqft: input.printCostCentsPerSqft,
        designMode,
        designFlatCents: input.designFlatCents,
        designMinutes: input.designMinutes,
        revisionRounds: Math.round(Number(revisionRounds) || 0),
        removalTier,
        fixedAddOnCents: input.fixedAddOnCents,
      }
    : null;

  const draftState: PrintDraftState = {
    vehicle, designId, coverageLevel, spotSqft, spotMinutes, printMediaItemId, laminateItemId, printCostPerSqft,
    designMode, designFlatCents, designMinutes, revisionRounds, removalTier, fixedAddOnCents,
  };
  const draftStateKey = JSON.stringify(draftState);
  useEffect(() => {
    if (editingLine || !vehicle) return;
    onStateChange?.(draftState as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStateKey, editingLine]);

  function handleAddLine() {
    if (!liveOutput) return;
    onAddLine(liveOutput);
    toast.success(editingLine ? t('pricing.lineUpdatedToast') : t('pricing.print.toastAdded'));

    if (!editingLine) {
      setVehicle(null);
      setDesignId(null);
      setCoverageLevel('full');
      setSpotSqft('');
      setSpotMinutes('');
      setLaminateItemId('');
      setPrintCostPerSqft('0');
      setDesignMode('waived');
      setDesignFlatCents('0');
      setDesignMinutes('0');
      setRemovalTier('none');
      setFixedAddOnCents('0');
    }
  }

  function goCreateDesign() {
    if (!customerVehicleHint) return;
    const params = new URLSearchParams();
    if (customerVehicleHint.year) params.set('year', String(customerVehicleHint.year));
    params.set('make', customerVehicleHint.make);
    params.set('model', customerVehicleHint.model);
    if (customerVehicleHint.id) params.set('customerVehicleId', customerVehicleHint.id);
    if (customerVehicleHint.color) params.set('color', customerVehicleHint.color);
    params.set('returnTo', location.pathname + location.search);
    navigate(`/print-wraps/new?${params.toString()}`);
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.vehicle')}</h3>
        {resolvingEditVehicle ? (
          <p className="text-sm text-muted">{t('pricing.loadingVehicle')}</p>
        ) : (
          <VehiclePicker workspaceId={workspaceId} selection={vehicle} onSelect={setVehicle} customerVehicleHint={customerVehicleHint} customerId={customerId} />
        )}
      </section>

      {vehicle ? (
        <>
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.print.designsTitle')}</h3>
            {!vehicleKey ? (
              <p className="text-sm text-muted">{t('pricing.print.noVehicleHint')}</p>
            ) : designs.length === 0 ? (
              <div className="flex flex-col gap-1.5 border border-border p-3">
                <p className="text-sm text-muted">{t('pricing.print.noDesigns')}</p>
                <Button type="button" size="sm" variant="secondary" className="w-fit" onClick={goCreateDesign}>
                  {t('pricing.print.createDesign')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {designs.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setDesignId(d.id === designId ? null : d.id)}
                    className={`flex items-center gap-3 border px-3 py-2 text-left text-sm ${designId === d.id ? 'border-accent bg-accent/10 font-semibold' : 'border-border hover:bg-surface-2'}`}
                  >
                    {d.latestRenderUrl || d.artworkUrl ? (
                      <img src={d.latestRenderUrl ?? d.artworkUrl ?? undefined} alt="" className="h-10 w-10 shrink-0 object-cover" />
                    ) : (
                      <div className="h-10 w-10 shrink-0 bg-surface-2" />
                    )}
                    <span className="flex flex-col">
                      <span>{d.name}</span>
                      <span className="text-xs font-normal text-muted">{t(PRINT_COVERAGE_LABEL_KEYS[d.coverageLevel])} · {t(`pricing.print.designStatus.${d.status}`)}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.print.coverageTitle')}</h3>
            <div className="flex flex-wrap gap-1.5">
              {(['full', 'three_quarter', 'half', 'spot'] as const).map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setCoverageLevel(level)}
                  className={`border px-3 py-1.5 text-xs font-semibold ${coverageLevel === level ? 'border-ink bg-ink text-surface' : 'border-border text-muted'}`}
                >
                  {t(PRINT_COVERAGE_LABEL_KEYS[level])}
                </button>
              ))}
            </div>
            {coverageLevel === 'spot' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="print-spot-sqft">{t('pricing.print.spotSqft')}</Label>
                  <Input id="print-spot-sqft" type="number" min="0" step="any" value={spotSqft} onChange={(e) => setSpotSqft(e.target.value)} />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="print-spot-minutes">{t('pricing.print.spotMinutes')}</Label>
                  <Input id="print-spot-minutes" type="number" min="0" step="1" value={spotMinutes} onChange={(e) => setSpotMinutes(e.target.value)} />
                </div>
              </div>
            ) : null}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.print.printMediaTitle')}</h3>
              <button type="button" onClick={() => setAddInventoryOpen('media')} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('pricing.material.addToInventory')}
              </button>
            </div>
            {printMediaItems.length === 0 ? (
              <p className="text-sm text-muted">{t('pricing.print.noInventoryItems')}</p>
            ) : (
              <select
                value={printMediaItemId}
                onChange={(e) => setPrintMediaItemId(e.target.value)}
                className="h-9 border border-border bg-surface px-2 text-sm"
              >
                {printMediaItems.map((i) => (
                  <option key={i.id} value={i.id}>{itemLabel(i.id)}</option>
                ))}
              </select>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.print.laminateTitle')}</h3>
              <button type="button" onClick={() => setAddInventoryOpen('laminate')} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('pricing.material.addToInventory')}
              </button>
            </div>
            <select
              value={laminateItemId}
              onChange={(e) => setLaminateItemId(e.target.value)}
              className="h-9 border border-border bg-surface px-2 text-sm"
            >
              <option value="">{t('pricing.print.noLaminate')}</option>
              {printMediaItems.map((i) => (
                <option key={i.id} value={i.id}>{itemLabel(i.id)}</option>
              ))}
            </select>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="print-outsourced-cost">{t('pricing.print.printCostPerSqft')}</Label>
              <Input id="print-outsourced-cost" type="number" min="0" step="0.01" value={printCostPerSqft} onChange={(e) => setPrintCostPerSqft(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="print-removal-tier">{t('pricing.print.removalTierLabel')}</Label>
              <select
                id="print-removal-tier"
                value={removalTier}
                onChange={(e) => setRemovalTier(e.target.value as typeof removalTier)}
                className="h-9 border border-border bg-surface px-2 text-sm"
              >
                {(['none', 'light', 'moderate', 'heavy_baked'] as const).map((tier) => (
                  <option key={tier} value={tier}>{t(`pricing.removalTier.${tier}`)}</option>
                ))}
              </select>
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.print.designFeeTitle')}</h3>
            <div className="flex flex-wrap gap-1.5">
              {(['waived', 'flat', 'hourly'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setDesignMode(mode)}
                  className={`border px-3 py-1.5 text-xs font-semibold ${designMode === mode ? 'border-ink bg-ink text-surface' : 'border-border text-muted'}`}
                >
                  {t(`pricing.print.designMode.${mode}`)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {designMode === 'flat' ? (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="print-design-flat">{t('pricing.print.designFlatCents')}</Label>
                  <Input id="print-design-flat" type="number" min="0" step="0.01" value={designFlatCents} onChange={(e) => setDesignFlatCents(e.target.value)} />
                </div>
              ) : null}
              {designMode === 'hourly' ? (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="print-design-minutes">{t('pricing.print.designMinutes')}</Label>
                  <Input id="print-design-minutes" type="number" min="0" step="1" value={designMinutes} onChange={(e) => setDesignMinutes(e.target.value)} />
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <Label htmlFor="print-revision-rounds">{t('pricing.print.revisionRounds')}</Label>
                <Input id="print-revision-rounds" type="number" min="0" step="1" value={revisionRounds} onChange={(e) => setRevisionRounds(e.target.value)} />
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-1">
            <Label htmlFor="print-addon-cents">{t('pricing.print.fixedAddOnCents')}</Label>
            <Input id="print-addon-cents" type="number" min="0" step="0.01" value={fixedAddOnCents} onChange={(e) => setFixedAddOnCents(e.target.value)} className="max-w-[10rem]" />
          </div>

          {result ? <PricingBreakdownSidebar result={result} /> : null}
          {printMediaItem ? (
            <p className="text-xs text-muted">
              {t('pricing.materialConsumption.breakdownRow', {
                qty: liveOutput?.materialQty ?? 0,
                unit: printMediaItem.unit,
                price: formatMoney(printMediaItem.priceCentsPerUnit),
                cost: formatMoney(result?.materialCostCents ?? 0),
              })}
            </p>
          ) : null}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={!canAdd} onClick={handleAddLine}>
              {editingLine ? t('pricing.saveChanges') : t('pricing.addService')}
            </Button>
            {editingLine ? (
              <Button type="button" variant="secondary" onClick={onCancelEdit}>
                {t('pricing.cancelEdit')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t('pricing.pickVehicle')}</p>
      )}

      <AddInventoryItemDialog
        open={addInventoryOpen !== null}
        onOpenChange={(v) => !v && setAddInventoryOpen(null)}
        workspaceId={workspaceId}
        category="print"
        onCreated={(itemId) => {
          if (addInventoryOpen === 'laminate') setLaminateItemId(itemId);
          else setPrintMediaItemId(itemId);
          setAddInventoryOpen(null);
        }}
      />
    </div>
  );
}
